"use client";

import { useEffect } from "react";
import posthog from "posthog-js";
import { isStaleBundleError, shouldAutoReloadStaleBundle } from "@/lib/errors/stale-bundle";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error("[GlobalError]", error);
    // Machine-translation detection (Chrome/Edge Translate mutates the DOM
    // and is the dominant third-party cause of NotFoundError crashes).
    const html = typeof document !== "undefined" ? document.documentElement : null;
    const translated = Boolean(
      html &&
        (html.classList.contains("translated-ltr") ||
          html.classList.contains("translated-rtl") ||
          document.querySelector("[_msttexthash]") !== null)
    );
    // Stale bundle after a deploy (chunk skew): hard-reload ONCE to fetch fresh
    // HTML + chunks. Guarded against loops; reported before reloading.
    let autoReloaded = false;
    if (isStaleBundleError(error) && typeof window !== "undefined") {
      // Garde temporel partagé (1 reload / 2 min) — voir stale-bundle.ts.
      autoReloaded = shouldAutoReloadStaleBundle();
    }
    try {
      posthog.captureException(error, {
        digest: error.digest,
        boundary: "global",
        translated,
        auto_reloaded: autoReloaded,
      });
    } catch {
      /* fail soft */
    }
    // Discord notification — server route rate-limits + fails soft.
    // Pas de ping quand l'auto-reload va guérir (voir error.tsx) ; PostHog
    // capture chaque occurrence dans tous les cas.
    if (typeof window !== "undefined" && !autoReloaded) {
      void fetch("/api/log/error", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          digest: error.digest,
          message: error.message,
          stack: error.stack,
          url: window.location.href,
          boundary: "global",
          translated,
          auto_reloaded: autoReloaded,
        }),
        keepalive: true,
      }).catch(() => {});
    }
    if (autoReloaded) {
      window.location.reload();
    }
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          minHeight: "100vh",
          margin: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "system-ui, -apple-system, sans-serif",
          background: "#0a0a0a",
          color: "#fafafa",
        }}
      >
        <div style={{ textAlign: "center", padding: 24, maxWidth: 480 }}>
          <h1 style={{ fontSize: 28, marginBottom: 12, fontWeight: 600 }}>
            Critical error / Erreur critique
          </h1>
          <p style={{ color: "#a3a3a3", marginBottom: 20, lineHeight: 1.5 }}>
            The application failed to start correctly. The incident has been
            logged and we are looking into it.
          </p>
          <p style={{ color: "#a3a3a3", marginBottom: 20, lineHeight: 1.5 }}>
            L&apos;application n&apos;a pas pu démarrer correctement. L&apos;incident a été
            enregistré, nous nous en occupons.
          </p>
          <button
            onClick={reset}
            style={{
              padding: "10px 20px",
              borderRadius: 8,
              background: "#fafafa",
              color: "#0a0a0a",
              border: "none",
              cursor: "pointer",
              fontWeight: 500,
              fontSize: 14,
            }}
          >
            Retry / Réessayer
          </button>
        </div>
      </body>
    </html>
  );
}
